<template>
  <SectionContainer
    v-if="section.type === 'section'"
    :bg-color="
      section.bgColor.value === 'none' ? undefined : section.bgColor.value
    "
    :height="
      isHeaderBlock
        ? 'none'
        : section.height.value === 'default'
          ? undefined
          : section.height.value
    "
    :size="
      isHeaderBlock || section.size.value === 'default'
        ? undefined
        : section.size.value
    "
  >
    <ContentBlockRenderer
      v-for="block in section.content"
      :key="block.id"
      :author
      :block
      :color="sectionTextColor(section.bgColor.value)"
      :fallback-form-url
      :geo-product-slug
      :has-local-search
      :last-update
      :parent-breadcrumbs
    />
  </SectionContainer>

  <SectionContainer
    v-else-if="section.type === 'headerSection'"
    :bg-color="
      section.bgColor.value === 'none' ? undefined : section.bgColor.value
    "
    class="header-section"
    height="none"
  >
    <SBreadcrumbsSeo :breadcrumbs="headerBreadcrumbs" />
    <ContentInfoPageHeader
      v-if="headerBlock"
      :author
      :description="headerBlock.description"
      :form-url="resolveHeaderFormUrl(headerBlock.formSlug)"
      :last-update="lastUpdate"
      :price-average="headerBlock.priceAverage"
      :price-maximum="headerBlock.priceMaximum"
      :price-minimum="headerBlock.priceMinimum"
      :stats="headerBlock.stats"
      :title="headerBlock.title"
    />
  </SectionContainer>

  <SectionCustomHtml
    v-else-if="section.type === 'customHtmlSection'"
    :section
  />

  <ContentJumplinks
    v-else-if="section.type === 'jumplinksSection'"
    :bg-color="
      section.bgColor.value === 'none' ? undefined : section.bgColor.value
    "
    :jumplinks
    :size="section.size.value === 'default' ? undefined : section.size.value"
  />

  <template v-else-if="section.type === 'partialSection'">
    <SectionRenderer
      v-for="nestedSection in section.entry.sections"
      :key="nestedSection.id"
      :author
      :fallback-form-url
      :geo-product-slug
      :has-local-search
      :jumplinks
      :last-update
      :parent-breadcrumbs
      :section="nestedSection"
    />
  </template>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { Jumplink } from "@solvari/statamic";

import type { Section } from "~/features/product/api/cms.api.ts";

import ContentBlockRenderer from "~/shared/components/content-blocks/ContentBlockRenderer.vue";
import ContentInfoPageHeader from "~/shared/components/content-blocks/ContentInfoPageHeader.vue";
import ContentJumplinks from "~/shared/components/content-blocks/ContentJumplinks.vue";
import SectionCustomHtml from "~/shared/components/content-blocks/SectionCustomHtml.vue";
import SBreadcrumbsSeo from "~/shared/components/SBreadcrumbsSeo.vue";
import SectionContainer from "~/shared/components/SectionContainer.vue";
import { ziggy } from "~/shared/libs/ziggy.ts";

type BreadcrumbLink = { href: string; label: string };
type HeaderBlock = Extract<
  Section,
  { type: "headerSection" }
>["content"][number];

const props = defineProps<{
  author?: string | null;
  fallbackFormUrl?: string | null;
  geoProductSlug?: string | null;
  hasLocalSearch?: boolean;
  jumplinks: Jumplink[];
  lastUpdate: string;
  parentBreadcrumbs?: BreadcrumbLink[];
  section: Section;
}>();

const headerBlock = computed<HeaderBlock | null>(() => {
  if (props.section.type !== "headerSection") {
    return null;
  }
  return props.section.content[0] ?? null;
});

const isHeaderBlock = computed(() => {
  if (props.section.type !== "section") {
    return false;
  }
  return props.section.content[0]?.type === "productHero";
});

const headerBreadcrumbs = computed(() => {
  if (!headerBlock.value) {
    return [];
  }
  return [
    { label: "Home", href: ziggy("mainSite.homepage") },
    ...(props.parentBreadcrumbs ?? []),
    { label: headerBlock.value.title },
  ];
});

function sectionTextColor(bgColor: string): "gray" | "white" {
  return bgColor === "primary" ? "white" : "gray";
}

function resolveHeaderFormUrl(formSlug: string | null): string | null {
  if (formSlug && props.geoProductSlug) {
    return ziggy("mainSite.geoProduct.leadForm", {
      geoProductSlug: props.geoProductSlug,
      formSlug,
    });
  }
  return props.fallbackFormUrl ?? null;
}
</script>
